import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  getCreatorOnboardingToken,
  getCreatorToken,
  removeCreatorOnboardingToken,
  removeCreatorToken,
  setCreatorOnboardingToken,
  setCreatorToken
} from '../services/sessionStorage';
import { logoutSession } from '../services/authService';

const AuthContext = createContext(null);
const SESSION_ENDING_CODES = new Set([
  'TOKEN_EXPIRED',
  'INVALID_TOKEN',
  'TOKEN_VALIDATION_FAILED',
  'CREATOR_NOT_FOUND',
  'WRONG_ROLE'
]);

export function AuthProvider({ children }) {
  const [creator, setCreator] = useState(null);
  const [token, setToken] = useState(null);
  const [onboardingToken, setOnboardingTokenState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prefetchData, setPrefetchData] = useState(null);
  const [authError, setAuthError] = useState(null);
  const verifyRequest = useRef(0);

  const verify = async (t) => {
    const request = ++verifyRequest.current;
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const headers = t ? { Authorization: `Bearer ${t}` } : undefined;
      const res = await axios.get(`${API_URL}/creator-portal/auth/verify`, {
        headers,
        withCredentials: true
      });
      if (request !== verifyRequest.current || (t || null) !== getCreatorToken()) return;
      if (!res.data?.creator?.id) {
        throw new Error('Invalid auth verify response');
      }
      setCreator(res.data.creator);
      // Store prefetched data so DataContext can pick it up
      setPrefetchData(res.data.prefetch || null);
      setAuthError(null);
      return res.data.creator;
    } catch (error) {
      if (request !== verifyRequest.current || (t || null) !== getCreatorToken()) throw error;
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        removeCreatorToken();
        setToken(null);
        setCreator(null);
      } else {
        setAuthError('We could not refresh your session. Please retry in a moment.');
      }
      throw error;
    }
  };

  useEffect(() => {
    const handleUnauthorized = (event) => {
      if (!SESSION_ENDING_CODES.has(event.detail?.code)) return;
      if (event.detail?.token && event.detail.token !== getCreatorToken()) return;
      verifyRequest.current += 1;
      removeCreatorToken();
      setToken(null);
      setCreator(null);
      setPrefetchData(null);
      setAuthError(null);
    };

    window.addEventListener('creator-auth:unauthorized', handleUnauthorized);

    const stored = getCreatorToken();
    const storedOnboarding = getCreatorOnboardingToken();
    if (storedOnboarding) {
      setOnboardingTokenState(storedOnboarding);
      setLoading(false);
      return () => window.removeEventListener('creator-auth:unauthorized', handleUnauthorized);
    } else if (stored) {
      setToken(stored);
      removeCreatorOnboardingToken();
      setOnboardingTokenState(null);
      verify(stored).catch(() => null).finally(() => setLoading(false));
    } else {
      verify(null).catch(() => null).finally(() => setLoading(false));
    }

    return () => window.removeEventListener('creator-auth:unauthorized', handleUnauthorized);
  }, []);

  const login = async (t) => {
    setCreatorToken(t);
    setToken(t);
    setOnboardingTokenState(null);
    await verify(t);
  };

  const startOnboarding = (t) => {
    verifyRequest.current += 1;
    setCreatorOnboardingToken(t);
    setOnboardingTokenState(t);
    setToken(null);
    setCreator(null);
    setPrefetchData(null);
    setAuthError(null);
  };

  const clearOnboarding = () => {
    removeCreatorOnboardingToken();
    setOnboardingTokenState(null);
  };

  const logout = () => {
    verifyRequest.current += 1;
    removeCreatorToken();
    removeCreatorOnboardingToken();
    setToken(null);
    setOnboardingTokenState(null);
    setCreator(null);
    setPrefetchData(null);
    setAuthError(null);
    logoutSession().catch(() => null);
  };

  const retryAuth = async () => {
    const stored = getCreatorToken();
    setLoading(true);
    setToken(stored);
    try {
      await verify(stored || null);
    } catch {
      // verify() records the visible auth error for the shell.
    } finally {
      setLoading(false);
    }
  };

  const consumePrefetch = useCallback(() => {
    const data = prefetchData;
    setPrefetchData(null);
    return data;
  }, [prefetchData]);

  return (
    <AuthContext.Provider value={{
      creator,
      token,
      onboardingToken,
      loading,
      authError,
      login,
      startOnboarding,
      clearOnboarding,
      logout,
      retryAuth,
      consumePrefetch
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
